import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createContext, useContext, useEffect, useState } from "react";

import LOCAL_STORAGE from "@/constants/local-storage";
import ROUTE_URLS from "@/constants/route-urls";
import { Language, LanguageContextProps, LanguageProviderProps } from "@/types/language.types";

const DEFAULT_LANGUAGE: Language = "PT-BR";

export const LanguageContext = createContext<LanguageContextProps>({} as LanguageContextProps);

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [lang, setLang] = useState<Language>(DEFAULT_LANGUAGE);

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  useEffect(() => {
    if (typeof localStorage === "undefined") return;

    const localStorageLang = (localStorage.getItem(LOCAL_STORAGE.lang) as Language) || null;
    const langParam = params?.lang;
    const languageToSet = localStorageLang || (langParam as Language) || DEFAULT_LANGUAGE;

    if (lang !== languageToSet) {
      setLang(languageToSet);
    }

    if (localStorageLang !== languageToSet) {
      localStorage.setItem(LOCAL_STORAGE.lang, languageToSet);
    }

    if (pathname === "/") {
      const dashboardSalesUrl = ROUTE_URLS.salesDashboard(languageToSet);
      navigate(dashboardSalesUrl, { replace: true });
    }

    const currentLangInPath = params?.lang || DEFAULT_LANGUAGE;
    const newPathname = pathname?.replace(`/${currentLangInPath}`, `/${languageToSet}`);

    if (newPathname !== pathname) {
      navigate(newPathname, { replace: true });
    }
  }, [lang, params?.lang, pathname, navigate]);

  return <LanguageContext.Provider value={{ lang }}>{children}</LanguageContext.Provider>;
};

const useLanguage = () => useContext(LanguageContext);

export default useLanguage;
